<template>
    <div class="home-screen" ref="homeScreen" :class="{ 'disabled': disabled }">
        <div class="home-screen-header">
            <div class="title-section" ref="titleSection">
				<img class="logo" src="/images/Buzztime_tv_white.svg" alt="Buzztime">
            </div>
        </div>

        <AppGrid
            :apps="apps"
            :active-app-id="activeAppId"
            @set-active="(id) => $emit('set-active', id)"
            @launch-app="(id) => $emit('launch-app', id)"
            @set-default-app="$emit('set-default-app', $event)"
            ref="appGridRef"/>
    </div>
</template>

<script setup>
import {ref, onMounted, nextTick, onUnmounted, watch} from 'vue';
import gsap from 'gsap';
import AppGrid from "@/components/AppGrid.vue";
import Header from "@/components/Header.vue";
import {cleanupKeyboardNavigation} from "@/services/keyboard.js";
import {systemInfo} from "../systemInfo.js";

const props = defineProps({
    apps: {
        type: Array,
        required: true
    },
    activeAppId: {
        type: Number,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['set-active', 'launch-app', 'set-default-app', 'ready']);
const homeScreen = ref(null);
const titleSection = ref(null);
const appGridRef = ref(null);
const headerRef = ref(null);

// Method to get the app tile element by ID
const getAppTileElement = async (id) => {
    if (appGridRef.value) {
        try {
            // Always get the current position of the app tile
            const element = await appGridRef.value.getAppTileElement(id);
            console.log(`HomeScreen: Got app tile element for ID ${id}`);
            return element;
        } catch (error) {
            console.error(`HomeScreen: Error getting app tile element for ID ${id}:`, error);
            return null;
        }
    }
    console.warn(`HomeScreen: AppGrid ref not available for ID ${id}`);
    return null;
};

const revealedTiles = new WeakSet();

function currentAppTiles() {
    if (!appGridRef.value) return [];
    return Array.from(appGridRef.value.$el.querySelectorAll('.app-tile'));
}

function animateAppTiles(tiles, timeline, position) {
    const fresh = tiles.filter(tile => !revealedTiles.has(tile));
    if (!fresh.length) return;

    fresh.forEach(tile => revealedTiles.add(tile));
    gsap.set(fresh, {opacity: 0, scale: 0.8, y: 20});

    const vars = {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)"
    };

    if (timeline) timeline.to(fresh, vars, position);
    else gsap.to(fresh, vars);
}

// Animation for home screen appearance
function animateHomeScreen() {
    const tl = gsap.timeline();

    // Prepare home screen elements (initially invisible)
    gsap.set(titleSection.value, {opacity: 0, y: 30});

    // Animate title section
    tl.to(titleSection.value, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out"
    });

    // Animate app tiles with stagger
    animateAppTiles(currentAppTiles(), tl, "-=0.5");

    return tl;
}

watch(() => props.apps, () => {
    nextTick(() => animateAppTiles(currentAppTiles()));
}, {deep: true});

onMounted(() => {
    nextTick(() => {
        animateHomeScreen();
        emit('ready');
    });
});

onUnmounted(() => {
    // Clean up keyboard navigation
    cleanupKeyboardNavigation();
});

defineExpose({
    getAppTileElement
});
</script>

<style scoped>
.home-screen {
    width: calc(var(--cqw) * 100);
    height: calc(var(--cqh) * 100);
    display: flex;
    flex-direction: column;
    position: fixed;
    z-index: 100;
	/*pointer-events: none;*/
	/*cursor: none;*/
}

/*.home-screen.disabled {*/
/*    pointer-events: none;*/
/*}*/

.home-screen-header {
    opacity: 0.75 !important; /* slight transparency to avoid constant full bright pixels for OLED screens*/
}

.title-section {
    height: calc(var(--cqh) * 32);
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: calc(var(--spacing-grid) * 1.1);
}

.main-title {
    font-size: var(--font-size-home);
    font-weight: var(--font-weight-medium);
    text-align: center;
    background: white;
    -webkit-background-clip: text;
    text-shadow: var(--shadow-md);
}

.logo {
    width: auto;
    height: calc(var(--cqw) * 11);
    padding-right: var(--spacing-md);
    filter: drop-shadow(var(--shadow-md));
}
</style>